import { useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogAction,
  Button,
  TextField,
  Banner,
} from '@ubiquitel/ui';
import './index.scss';

interface AuthDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
}

const AuthDialog = ({ isOpen, onClose, title = 'Account' }: AuthDialogProps) => {
  const { isAuthenticated, signInWithPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMessage(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isAuthenticated && isOpen) {
      onClose();
    }
  }, [isAuthenticated, isOpen, onClose]);

  const resetFields = () => {
    setEmail('');
    setPassword('');
    setMessage(null);
    setIsSubmitting(false);
  };

  const closeDialog = () => {
    resetFields();
    onClose();
  };

  const submitSignIn = async () => {
    if (!email || !password) {
      setMessage('Enter your email and password.');
      return;
    }

    try {
      setIsSubmitting(true);
      await signInWithPassword(email, password);
      closeDialog();
    } catch (error) {
      setMessage((error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) closeDialog(); }}>
      <DialogContent heading={title} className="component--auth-dialog">
        <div className="component--auth-dialog__content">
          <TextField
            type="email"
            label="Email"
            placeholder="name@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            size="full"
          />
          <TextField
            type="password"
            label="Password"
            placeholder="Password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            size="full"
          />
          {message && <Banner variant="info" heading={message} dismissible={false} />}
          <DialogActions>
            <DialogAction variant="default" onClick={closeDialog}>Cancel</DialogAction>
            <Button onClick={submitSignIn} disabled={isSubmitting} loading={isSubmitting}>
              Sign in
            </Button>
          </DialogActions>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthDialog;
